// "Previous calls": every inspection, and what came out of it.
//
// The record is the call, not the recording. A call with no video is still a
// call worth reading - the transcript, what was extracted, what it was priced
// at - and with recording currently unavailable on the LiveSwitch plan, that is
// every call.

import { useCallback, useEffect, useState } from 'react';
import {
  InspectionsStore,
  type Inspection,
  type InspectionDetail,
  type Playback,
} from '../inspections/InspectionsStore';

const PAGE_SIZE = 25;

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'Unknown date';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'Unknown date';
  return when.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatAt(ms: number | null): string {
  if (ms === null) return '';
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const SOURCE_LABEL: Record<string, string> = {
  extracted: 'from the call',
  confirmed: 'confirmed',
  manual: 'entered by hand',
};

export function InspectionsView() {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InspectionDetail | null>(null);
  const [detailProblem, setDetailProblem] = useState<string | null>(null);
  const [playback, setPlayback] = useState<Playback | null>(null);
  const [cannotPlay, setCannotPlay] = useState(false);

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true);
    const { data, problem: failed } = await InspectionsStore.list(nextOffset, PAGE_SIZE);
    if (data) {
      setInspections(data.inspections);
      setTotal(data.total);
      setOffset(data.offset);
    }
    setProblem(failed);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load(0);
  }, [load]);

  const open = useCallback(
    async (inspection: Inspection) => {
      if (openId === inspection.id) {
        setOpenId(null);
        return;
      }
      setOpenId(inspection.id);
      setDetail(null);
      setDetailProblem(null);
      setPlayback(null);
      setCannotPlay(false);
      const { data, problem: failed } = await InspectionsStore.detail(inspection.id);
      setDetail(data);
      setDetailProblem(failed);
    },
    [openId],
  );

  const watch = useCallback(async (inspectionId: string, recordingId: string) => {
    setPlayback(null);
    setCannotPlay(false);
    const { data, problem: failed } = await InspectionsStore.playback(inspectionId, recordingId);
    setPlayback(data);
    if (failed) setDetailProblem(failed);
  }, []);

  const pages = Math.ceil(total / PAGE_SIZE);
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="prev">
      <div className="prev__head">
        <h2>Previous calls</h2>
        <button
          type="button"
          className="ghostbtn"
          disabled={loading}
          onClick={() => void load(offset)}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {problem && (
        <p className="notice notice--bad">
          {problem}{' '}
          <button type="button" className="ghostbtn" onClick={() => void load(offset)}>
            Try again
          </button>
        </p>
      )}

      {!loading && !problem && inspections.length === 0 && (
        <p className="empty-note">
          No calls yet. A call appears here as soon as it is joined, and fills in with the
          transcript and answers when it ends.
        </p>
      )}

      <ul className="prev__list">
        {inspections.map((inspection) => {
          const isOpen = openId === inspection.id;
          return (
            <li key={inspection.id} className={`prevrow${isOpen ? ' prevrow--open' : ''}`}>
              <button type="button" className="prevrow__top" onClick={() => void open(inspection)}>
                <span className="prevrow__when">{formatWhen(inspection.startedAt)}</span>
                <span className="prevrow__meta">
                  {formatDuration(inspection.durationSeconds)}
                  {inspection.checklistName ? ` · ${inspection.checklistName}` : ''}
                  {inspection.phoneNumber ? ` · ${inspection.phoneNumber}` : ''}
                </span>
                <span className="prevrow__meta">
                  {inspection.fieldCount > 0
                    ? `${inspection.answeredCount}/${inspection.fieldCount} answered`
                    : 'No checklist'}
                  {inspection.acceptedBy ? ` · ${inspection.acceptedBy}` : ''}
                </span>
                {/* The list said nothing about recordings, so the only way to
                    find out a call had video was to open it. The API has always
                    returned this; the row simply ignored it. */}
                <span className="prevrow__rec">
                  {inspection.recordingStatus === 'stored'
                    ? '▶ Video'
                    : inspection.recordingStatus === 'pending'
                      ? 'Video copying'
                      : inspection.recordingStatus === 'failed'
                        ? 'Video failed'
                        : ''}
                </span>
                <span className="prevrow__price">
                  {inspection.estimatedPriceCents !== null
                    ? money.format(inspection.estimatedPriceCents / 100)
                    : ''}
                </span>
                <span className="prevrow__state">
                  {/* No end time means the call was joined and never finished -
                      either it is happening now, or it ended in a way that
                      never got to say so. Both are worth showing as different
                      from a completed call. */}
                  {inspection.endedAt === null ? 'In progress' : isOpen ? 'Close' : 'Open'}
                </span>
              </button>

              {isOpen && (
                <div className="prevrow__body">
                  {detailProblem && <p className="note">{detailProblem}</p>}
                  {!detail && !detailProblem && <p className="note">Loading…</p>}

                  {detail && (
                    <>
                      {detail.fields.length > 0 && (
                        <div className="prevfields">
                          {detail.fields.map((field) => (
                            <div className="prevfield" key={`${field.fieldKey}-${field.label}`}>
                              <span className="prevfield__label">{field.label}</span>
                              <span
                                className={`prevfield__value${
                                  field.value ? '' : ' prevfield__value--empty'
                                }`}
                              >
                                {field.value ?? 'Not answered'}
                              </span>
                              {field.value && (
                                <span className="prevfield__source">
                                  {SOURCE_LABEL[field.source] ?? field.source}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {detail.recordings.length > 0 && (
                        <div className="prevrec">
                          {detail.recordings.map((recording) => (
                            <div key={recording.id}>
                              {recording.status === 'stored' ? (
                                <button
                                  type="button"
                                  className="ghostbtn"
                                  onClick={() => void watch(detail.id, recording.id)}
                                >
                                  Watch recording
                                </button>
                              ) : (
                                <p className="note">
                                  {recording.status === 'pending'
                                    ? 'A recording is still being copied from LiveSwitch.'
                                    : (recording.problem ?? 'A recording could not be copied.')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {playback && (
                        <>
                          {/* VP8/Opus in Matroska: Chrome and Firefox play it,
                              Safari plays neither container. The download sits
                              beside the player rather than behind a failure. */}
                          <video
                            className="prevrow__video"
                            src={playback.playbackUrl}
                            controls
                            preload="metadata"
                            onError={() => setCannotPlay(true)}
                          />
                          {cannotPlay && (
                            <p className="note">
                              This browser cannot play the recording&rsquo;s format. Download it and
                              open it in VLC, or try Chrome.
                            </p>
                          )}
                          <p className="prevrow__actions">
                            <a className="ghostbtn" href={playback.downloadUrl}>
                              Download
                            </a>
                            <span className="prevrow__note">
                              This link stops working in an hour.
                            </span>
                          </p>
                        </>
                      )}

                      {detail.transcript.length > 0 ? (
                        <div className="prevtranscript">
                          {detail.transcript.map((line, index) => (
                            <p key={index}>
                              <span className="prevtranscript__at">{formatAt(line.atMs)}</span>
                              {line.text}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="empty-note">No transcript was captured for this call.</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {pages > 1 && (
        <div className="prev__pager">
          <button
            type="button"
            className="ghostbtn"
            disabled={offset === 0 || loading}
            onClick={() => void load(Math.max(0, offset - PAGE_SIZE))}
          >
            Newer
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="ghostbtn"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => void load(offset + PAGE_SIZE)}
          >
            Older
          </button>
        </div>
      )}
    </div>
  );
}
